import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Plus,
  Minus,
  Trash2,
  X,
  AlertTriangle,
  Send,
  Save,
  Building2,
  CreditCard,
  FileText,
  SlidersHorizontal,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import NewProductForm from "./NewProductForm";
import { api, formatApiErrorDetail } from "../lib/api";
import { OptionPickerModal } from "./OptionPickerModal";
import { computeOptionExtras } from "../lib/menuOptions";
import { US_STATES, ZIP_RE } from "../lib/usStates";

const TAX_RATE = 0.08875;
const STATUSES = ["pending", "submitted", "confirmed", "fulfilled", "paid", "cancelled"];

const emptyForm = () => ({
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  address: "",
  address_line2: "",
  city: "",
  state: "",
  zip_code: "",
  fulfillment: "pickup",
  delivery_fee: 0,
  event_date: "",
  event_time: "",
  on_site_contact_name: "",
  on_site_contact_phone: "",
  delivery_instructions: "",
  has_receiving_personnel: null,
  has_delivery_cart: null,
  notes: "",
  internal_notes: "",
  status: "pending",
  po_number: "",
  payment_method: "po",
  role_pricing: "private",
  discount_amount: 0,
  discount_label: "",
  tax_exempt: false,
  items: [],
  // Local-only field — the picked customer's user_id, used to link the
  // freshly-created invoice into their /account/invoices view via a
  // secondary POST after create. Never sent as part of the initial create
  // payload (backend doesn't accept it on that route).
  _assigned_user_id: null,
});

/**
 * Modal used for both EDIT (existing order) and CREATE (new manual invoice).
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (v) => void
 *   menu: { items: [...] }   — full catalog used to populate the picker
 *   order: existing order doc (edit mode), or null (create mode)
 *   prefill: partial form fields to seed *create* mode (used when
 *            converting a customer's cart quote into a draft invoice).
 *            Ignored when `order` is provided.
 *   onSaved: (order) => void — called with the new/updated order
 */
export const OrderEditorModal = ({ open, onOpenChange, menu, order, prefill, onSaved }) => {
  const mode = order ? "edit" : "create";
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  // Clicking a category chip narrows the catalog picker in one tap. Empty
  // string (= "All") shows every category.
  const [activeCategory, setActiveCategory] = useState("");
  // Newly-created menu items — merged into the picker so the operator can
  // immediately drop them onto the invoice without waiting for a parent
  // refresh. Cleared when the modal is closed.
  const [localAdditions, setLocalAdditions] = useState([]);
  // Inline "create product" mini-form state. Kept minimal (no image upload,
  // no modifiers) so it's a 5-second flow — full editing lives on the Menu tab.
  const [createOpen, setCreateOpen] = useState(false);

  // ------------------------------------------------------------------
  // "Assign to an existing customer" picker
  // ------------------------------------------------------------------
  // Rather than retype the name/email/address for a repeat customer, the
  // admin can pick from a searchable dropdown of agencies + private
  // customers already in the system. Picking one auto-fills the contact
  // fields, selects the right role_pricing, and (for agencies) turns on
  // tax-exempt automatically.
  const [customers, setCustomers] = useState({ agencies: [], private: [] });
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const [ag, pv] = await Promise.all([
          api.get("/api/admin/agencies?search="),
          api.get("/api/admin/private-customers"),
        ]);
        if (cancelled) return;
        const agencies = (ag.data?.items || ag.data?.agencies || (Array.isArray(ag.data) ? ag.data : []) || []).map(
          (a) => ({
            id: a.id,
            email: a.email,
            name: a.name,
            organization: a.organization || "",
            role: "agency",
            phone: a.phone || "",
          }),
        );
        const privates = (pv.data?.items || (Array.isArray(pv.data) ? pv.data : []) || []).map(
          (p) => ({
            id: p.id || p.user_id,
            email: p.email,
            name: p.name,
            organization: "",
            role: "private",
            phone: p.phone || "",
            address: p.address,
            city: p.city,
            state: p.state,
            zip_code: p.zip_code,
          }),
        );
        setCustomers({ agencies, private: privates });
      } catch (err) {
        // Non-blocking — admin can still create fresh contact info by hand.
        // eslint-disable-next-line no-console
        console.warn("[order-editor] customer directory load failed:", err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const customerResults = useMemo(() => {
    const term = customerSearch.trim().toLowerCase();
    const all = [...customers.agencies, ...customers.private];
    if (!term) return all.slice(0, 25);
    return all.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(term) ||
        (c.email || "").toLowerCase().includes(term) ||
        (c.organization || "").toLowerCase().includes(term),
    ).slice(0, 25);
  }, [customers, customerSearch]);

  const applyCustomerPreset = (c) => {
    setForm((s) => ({
      ...s,
      customer_name: c.organization || c.name || s.customer_name,
      customer_email: c.email || s.customer_email,
      customer_phone: c.phone || s.customer_phone,
      address: c.address ?? s.address,
      city: c.city ?? s.city,
      state: c.state ?? s.state,
      zip_code: c.zip_code ?? s.zip_code,
      role_pricing: c.role === "agency" ? "agency" : "private",
      tax_exempt: c.role === "agency" ? true : s.tax_exempt,
      // Link the invoice to their user account so it shows up in their
      // /account/invoices view.
      _assigned_user_id: c.id || null,
    }));
    setCustomerPickerOpen(false);
    setCustomerSearch("");
  };

  // Hydrate when opened
  useEffect(() => {
    if (!open) return;
    if (order) {
      setForm({
        customer_name: order.customer_name || "",
        customer_email: order.customer_email || "",
        customer_phone: order.customer_phone || "",
        address: order.address || "",
        address_line2: order.address_line2 || "",
        city: order.city || "",
        state: order.state || "",
        zip_code: order.zip_code || "",
        fulfillment: order.fulfillment || "pickup",
        delivery_fee: order.delivery_fee || 0,
        event_date: order.event_date || "",
        event_time: order.event_time || "",
        on_site_contact_name: order.on_site_contact_name || "",
        on_site_contact_phone: order.on_site_contact_phone || "",
        delivery_instructions: order.delivery_instructions || "",
        has_receiving_personnel:
          order.has_receiving_personnel === true || order.has_receiving_personnel === false
            ? order.has_receiving_personnel
            : null,
        has_delivery_cart:
          order.has_delivery_cart === true || order.has_delivery_cart === false
            ? order.has_delivery_cart
            : null,
        notes: order.notes || "",
        internal_notes: order.internal_notes || "",
        status: order.status || "pending",
        po_number: order.po_number || "",
        payment_method:
          order.type === "stripe"
            ? "card"
            : order.type === "cash"
            ? "cash"
            : "po",
        role_pricing: order.type === "agency_po" ? "agency" : "private",
        discount_amount: order.discount_amount || 0,
        discount_label: order.discount_label || "",
        tax_exempt: !!order.tax_exempt,
        items: (order.items || []).map((it) => ({
          package_id: it.package_id || it.id,
          quantity: it.quantity || 1,
          selected_options: it.selected_options,
          unit_price_override: it.price_override ? it.unit_price : undefined,
        })),
      });
    } else {
      // Create mode. Seed from `prefill` when supplied (e.g. converting a
      // small-order cart quote into a draft invoice).
      if (prefill) {
        const base = emptyForm();
        setForm({
          ...base,
          ...prefill,
          items: Array.isArray(prefill.items) ? prefill.items : [],
        });
      } else {
        setForm(emptyForm());
      }
    }
    setError("");
    setSearch("");
    setLocalAdditions([]);
    setCreateOpen(false);
  }, [open, order, prefill]);

  const update = (k) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm((s) => ({ ...s, [k]: val }));
  };

  const itemPrice = (pkgId) => {
    const found =
      localAdditions.find((m) => m.id === pkgId) ||
      menu?.items?.find((m) => m.id === pkgId);
    if (!found) return 0;
    const base = found.price || found.private_price || 0;
    if (form.role_pricing !== "agency") return base;
    // Category-specific markup override (e.g. Beverages @ 1.10) beats the
    // global default. Server-side pricing is authoritative — this is only
    // for the admin's in-editor preview.
    const cat = found.category;
    const byCat = menu?.agency_markup_by_category || {};
    const markup = (cat && byCat[cat]) || menu?.agency_markup || 1.2;
    return Math.round(base * markup * 100) / 100;
  };
  const itemName = (pkgId) =>
    localAdditions.find((m) => m.id === pkgId)?.name ||
    menu?.items?.find((m) => m.id === pkgId)?.name ||
    pkgId;
  const itemDef = (pkgId) =>
    localAdditions.find((m) => m.id === pkgId) ||
    menu?.items?.find((m) => m.id === pkgId);

  // Per-line unit price: admin override (if set) wins; otherwise
  // base × agency markup + modifier deltas.
  const lineUnitPrice = (line) => {
    if (
      line.unit_price_override !== null &&
      line.unit_price_override !== undefined &&
      line.unit_price_override !== ""
    ) {
      return Math.round(Number(line.unit_price_override) * 100) / 100;
    }
    const found = itemDef(line.package_id);
    if (!found) return 0;
    const base = itemPrice(line.package_id);
    return Math.round((base + computeOptionExtras(found, line.selected_options)) * 100) / 100;
  };

  // Generate a stable line key — same package with different modifiers
  // must be separate lines, otherwise their selections collide.
  const lineKey = (line) =>
    `${line.package_id}::${JSON.stringify(line.selected_options || {})}::${line.unit_price_override ?? ""}`;

  const setLineOverride = (key, raw) => {
    const v = raw === "" || raw == null ? undefined : Math.max(0, Number(raw) || 0);
    setForm((s) => ({
      ...s,
      items: s.items.map((it) =>
        lineKey(it) === key ? { ...it, unit_price_override: v } : it,
      ),
    }));
  };

  const totals = useMemo(() => {
    const subtotal = form.items.reduce(
      (s, it) => s + lineUnitPrice(it) * (Number(it.quantity) || 0),
      0
    );
    const discountInput = Number(form.discount_amount || 0);
    const discount = Math.max(0, Math.min(discountInput, subtotal));
    const taxable = Math.max(0, subtotal - discount);
    const delivery =
      form.fulfillment === "delivery" ? Number(form.delivery_fee || 0) : 0;
    const isExempt = !!form.tax_exempt || form.role_pricing !== "private";
    const taxRate = isExempt ? 0 : TAX_RATE;
    const tax = Math.round(taxable * taxRate * 100) / 100;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      discount: Math.round(discount * 100) / 100,
      delivery,
      tax,
      isExempt,
      total: Math.round((taxable + delivery + tax) * 100) / 100,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.items, form.fulfillment, form.delivery_fee, form.role_pricing, form.discount_amount, form.tax_exempt, menu]);

  const filteredCatalog = useMemo(() => {
    const term = search.trim().toLowerCase();
    const cat = activeCategory.trim().toLowerCase();
    // Local additions are prepended so newly-created products are
    // instantly visible at the top of the picker even before a page reload.
    const items = [...localAdditions, ...(menu?.items || []).filter((m) => !m.one_off)];
    let matched = items;
    if (cat) {
      matched = matched.filter(
        (m) => (m.category || "").toLowerCase() === cat,
      );
    }
    if (term) {
      matched = matched.filter(
        (m) =>
          m.name.toLowerCase().includes(term) ||
          (m.category || "").toLowerCase().includes(term),
      );
    }
    // Sort by category → name so items within a category appear together.
    // (Local additions retain top-of-list position via the prepend above,
    // then everything else is grouped for browsing.)
    return matched
      .slice()
      .sort((a, b) => {
        const ac = (a.category || "").toLowerCase();
        const bc = (b.category || "").toLowerCase();
        if (ac !== bc) return ac < bc ? -1 : 1;
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [menu, search, activeCategory, localAdditions]);

  // Pre-compute an array of {category, items[]} groups for the picker so we
  // can render sticky category headers above each block. Makes it easy to
  // spot categories at a glance (e.g. "Breakfast & Brunch") without needing
  // to search.
  const groupedCatalog = useMemo(() => {
    const groups = [];
    let current = null;
    for (const it of filteredCatalog) {
      const cat = it.category || "Uncategorized";
      if (!current || current.category !== cat) {
        current = { category: cat, items: [] };
        groups.push(current);
      }
      current.items.push(it);
    }
    return groups;
  }, [filteredCatalog]);

  // Categories drawn from the current catalog for the create-product form.
  const existingCategories = useMemo(() => {
    const set = new Set();
    for (const it of menu?.items || []) if (it.category) set.add(it.category);
    for (const it of localAdditions) if (it.category) set.add(it.category);
    return Array.from(set).sort();
  }, [menu, localAdditions]);

  // Modifier picker: which menu item are we configuring, and (for edits)
  // which line should the result replace?
  const [picker, setPicker] = useState(null);

  const addItem = (pkgId) => {
    const def = itemDef(pkgId);
    const hasOpts = Array.isArray(def?.options) && def.options.length > 0;
    if (hasOpts) {
      // Force the operator to pick modifiers so the receipt + kitchen
      // ticket carry sauce/style/etc.
      setPicker({ item: def });
      return;
    }
    setForm((s) => {
      const existing = s.items.find(
        (it) => it.package_id === pkgId && !it.selected_options,
      );
      if (existing) {
        return {
          ...s,
          items: s.items.map((it) =>
            it === existing ? { ...it, quantity: it.quantity + 1 } : it,
          ),
        };
      }
      return {
        ...s,
        items: [...s.items, { package_id: pkgId, quantity: 1 }],
      };
    });
  };

  const handlePickerConfirm = ({ selectedOptions, quantity }) => {
    const item = picker?.item;
    const editKey = picker?.editLineKey;
    setPicker(null);
    if (!item) return;
    const newLine = {
      package_id: item.id,
      quantity: Math.max(1, Number(quantity) || 1),
      selected_options: selectedOptions || {},
    };
    setForm((s) => {
      if (editKey) {
        const idx = s.items.findIndex((it) => lineKey(it) === editKey);
        if (idx >= 0) {
          const next = [...s.items];
          next[idx] = newLine;
          return { ...s, items: next };
        }
      }
      // Merge identical modifier combos rather than creating duplicates.
      const newKey = lineKey(newLine);
      const existingIdx = s.items.findIndex((it) => lineKey(it) === newKey);
      if (existingIdx >= 0) {
        const next = [...s.items];
        next[existingIdx] = {
          ...next[existingIdx],
          quantity: next[existingIdx].quantity + newLine.quantity,
        };
        return { ...s, items: next };
      }
      return { ...s, items: [...s.items, newLine] };
    });
  };

  const editLineModifiers = (line) => {
    const def = itemDef(line.package_id);
    if (!def) return;
    setPicker({
      item: def,
      editLineKey: lineKey(line),
      initialSelections: line.selected_options,
    });
  };

  const setQtyByKey = (key, qty) => {
    setForm((s) => ({
      ...s,
      items:
        qty < 1
          ? s.items.filter((it) => lineKey(it) !== key)
          : s.items.map((it) =>
              lineKey(it) === key ? { ...it, quantity: qty } : it,
            ),
    }));
  };

  const removeItemByKey = (key) => setQtyByKey(key, 0);

  const validate = () => {
    if (form.items.length === 0) return "Add at least one line item.";
    if (!form.customer_name.trim()) return "Customer name required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customer_email))
      return "Valid customer email required.";
    if (!form.customer_phone.trim()) return "Customer phone required.";
    if (form.fulfillment === "delivery" && !form.address.trim())
      return "Street address required.";
    if (form.fulfillment === "delivery" && !form.city.trim())
      return "City required.";
    if (form.fulfillment === "delivery" && !form.state.trim())
      return "State required.";
    if (form.fulfillment === "delivery" && form.zip_code && !ZIP_RE.test(form.zip_code.trim()))
      return "ZIP code must be 5 digits (e.g. 11433).";
    if (form.fulfillment === "delivery" && !form.zip_code.trim())
      return "ZIP code required.";
    if (mode === "create" && form.payment_method === "po" && !form.po_number.trim())
      return "PO Number required for PO invoice.";
    return "";
  };

  const submit = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError("");
    setSaving(true);
    try {
      if (mode === "create") {
        const payload = {
          role_pricing: form.role_pricing,
          payment_method: form.payment_method,
          items: form.items.map((it) => ({
            package_id: it.package_id,
            quantity: Number(it.quantity),
            selected_options: it.selected_options || null,
            unit_price_override:
              it.unit_price_override !== undefined && it.unit_price_override !== ""
                ? Number(it.unit_price_override)
                : null,
          })),
          customer_name: form.customer_name,
          customer_email: form.customer_email,
          customer_phone: form.customer_phone,
          address: form.address || null,
          address_line2: form.address_line2 || null,
          city: form.city || null,
          state: form.state || null,
          zip_code: form.zip_code || null,
          fulfillment: form.fulfillment,
          delivery_fee: Number(form.delivery_fee || 0),
          event_date: form.event_date || null,
          event_time: form.event_time || null,
          on_site_contact_name: form.on_site_contact_name || null,
          on_site_contact_phone: form.on_site_contact_phone || null,
          delivery_instructions: form.delivery_instructions || null,
          has_receiving_personnel: form.has_receiving_personnel,
          has_delivery_cart: form.has_delivery_cart,
          notes: form.notes || null,
          internal_notes: form.internal_notes || null,
          po_number: form.payment_method === "po" ? form.po_number : null,
          discount_amount: Number(form.discount_amount || 0),
          discount_label: form.discount_label || null,
        };
        const res = await api.post("/admin/orders", payload);
        const createdOrder = res.data.order;
        // Link to the picked customer's user account so it shows in their
        // /account/invoices view. Fire-and-forget — the order is already
        // saved; this just adds the link.
        if (form._assigned_user_id && createdOrder?.id) {
          try {
            await api.post(
              `/admin/orders/${createdOrder.id}/assign-agency`,
              { user_id: form._assigned_user_id },
            );
          } catch {
            /* non-blocking */
          }
        }
        onSaved?.(createdOrder);
      } else {
        const payload = {
          items: form.items.map((it) => ({
            package_id: it.package_id,
            quantity: Number(it.quantity),
            selected_options: it.selected_options || null,
            unit_price_override:
              it.unit_price_override !== undefined && it.unit_price_override !== ""
                ? Number(it.unit_price_override)
                : null,
          })),
          customer_name: form.customer_name,
          customer_email: form.customer_email,
          customer_phone: form.customer_phone,
          address: form.address || null,
          address_line2: form.address_line2 || null,
          city: form.city || null,
          state: form.state || null,
          zip_code: form.zip_code || null,
          fulfillment: form.fulfillment,
          delivery_fee: Number(form.delivery_fee || 0),
          event_date: form.event_date || null,
          event_time: form.event_time || null,
          on_site_contact_name: form.on_site_contact_name || null,
          on_site_contact_phone: form.on_site_contact_phone || null,
          delivery_instructions: form.delivery_instructions || null,
          has_receiving_personnel: form.has_receiving_personnel,
          has_delivery_cart: form.has_delivery_cart,
          notes: form.notes || null,
          internal_notes: form.internal_notes || null,
          status: form.status,
          po_number: form.po_number || null,
          discount_amount: Number(form.discount_amount || 0),
          discount_label: form.discount_label || null,
          tax_exempt: !!form.tax_exempt,
        };
        const res = await api.patch(`/admin/orders/${order.id}`, payload);
        onSaved?.(res.data.order);
      }
      onOpenChange(false);
    } catch (e) {
      setError(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          e?.message ||
          "Could not save"
      );
    } finally {
      setSaving(false);
    }
  };

  const sendInvoice = async () => {
    if (mode !== "edit" || !order?.id) return;
    setSending(true);
    setError("");
    try {
      await api.post(`/admin/orders/${order.id}/send-invoice`);
      onSaved?.({ ...order, last_invoice_sent_at: new Date().toISOString() });
      onOpenChange(false);
    } catch (e) {
      setError(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          e?.message ||
          "Could not send invoice"
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange} modal={!picker}>
      <DialogContent
        data-testid="order-editor-modal"
        className="sm:max-w-[820px] max-h-[92vh] overflow-y-auto p-0 rounded-2xl border-none"
        onPointerDownOutside={(e) => {
          // While the modifier picker is open, don't let Radix treat its
          // clicks as "outside" — that would close the entire editor and
          // swallow the modifier selection.
          if (picker) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (picker) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          // Let the picker handle its own Escape close first.
          if (picker) e.preventDefault();
        }}
      >
        {/* Header */}
        <div className="bg-brand-charcoal text-white px-6 py-5 sticky top-0 z-10">
          <DialogHeader className="space-y-1">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-gold">
              {mode === "create" ? "Create Invoice" : `Edit Order ${order?.id?.slice(0, 8) || ""}`}
            </p>
            <DialogTitle className="font-display font-black text-[22px] tracking-tight text-white text-left">
              {mode === "create" ? "Manual invoice / order" : "Update order details"}
            </DialogTitle>
            <DialogDescription className="text-[12.5px] text-white/65 text-left">
              {mode === "create"
                ? "Build an invoice manually. Totals + tax recompute live."
                : "Adjust items, contact, notes, or mark status. Saving recomputes totals."}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-5 sm:px-6 py-5 space-y-5">
          {/* Assign to an existing customer / agency. Optional — picking one
              auto-fills the contact fields + role_pricing + tax_exempt. */}
          {mode === "create" && (
            <SectionCard title="Assign to a customer">
              {form._assigned_user_id ? (
                <div className="flex items-center justify-between gap-3 rounded-xl bg-brand-teal/10 border border-brand-teal/40 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-brand-teal">
                      {form.role_pricing === "agency" ? "Agency · linked" : "Private customer · linked"}
                    </p>
                    <p className="mt-1 text-[13px] font-bold text-brand-charcoal truncate">
                      {form.customer_name} · {form.customer_email}
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid="editor-customer-clear"
                    onClick={() =>
                      setForm((s) => ({ ...s, _assigned_user_id: null }))
                    }
                    className="shrink-0 inline-flex items-center gap-1 rounded-full bg-white border border-black/10 px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.10em] hover:border-brand-teal"
                  >
                    Unlink
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    data-testid="editor-customer-search"
                    placeholder="Search agencies + private customers…"
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setCustomerPickerOpen(true);
                    }}
                    onFocus={() => setCustomerPickerOpen(true)}
                    className="w-full h-10 rounded-xl bg-white border border-black/10 px-3 text-[13px] focus:outline-none focus:border-brand-teal"
                  />
                  {customerPickerOpen && customerResults.length > 0 && (
                    <div
                      data-testid="editor-customer-picker"
                      className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto rounded-xl bg-white border border-black/10 shadow-lg"
                    >
                      {customerResults.map((c) => (
                        <button
                          key={`${c.role}-${c.id || c.email}`}
                          type="button"
                          data-testid={`editor-customer-option-${c.role}-${(c.id || c.email).slice(0, 8)}`}
                          onClick={() => applyCustomerPreset(c)}
                          className="flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-brand-cream border-b border-black/5 last:border-b-0"
                        >
                          <span
                            className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.12em] ${
                              c.role === "agency"
                                ? "bg-brand-teal/15 text-brand-teal"
                                : "bg-brand-gold/20 text-brand-gold"
                            }`}
                          >
                            {c.role === "agency" ? "Agency" : "Private"}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[13px] font-bold text-brand-charcoal truncate">
                              {c.organization || c.name}
                            </span>
                            <span className="block text-[11.5px] text-brand-charcoal/60 truncate">
                              {c.name && c.organization ? `${c.name} · ` : ""}
                              {c.email}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="mt-2 text-[11px] text-brand-charcoal/55">
                    Optional — leave blank to create for a brand-new customer.
                    Picking an agency auto-applies tax-exempt.
                  </p>
                </div>
              )}
            </SectionCard>
          )}

          {/* Pricing role (create only) */}
          {mode === "create" && (
            <SectionCard title="Invoice type">
              <div className="grid sm:grid-cols-2 gap-3">
                <PickerCard
                  testId="invoice-role-private"
                  active={form.role_pricing === "private"}
                  onClick={() =>
                    setForm((s) => ({ ...s, role_pricing: "private", tax_exempt: false }))
                  }
                  icon={CreditCard}
                  label="Private customer"
                  helper="Retail pricing · 8.875% NYC sales tax applied"
                />
                <PickerCard
                  testId="invoice-role-agency"
                  active={form.role_pricing === "agency"}
                  onClick={() =>
                    setForm((s) => ({ ...s, role_pricing: "agency", tax_exempt: true }))
                  }
                  icon={Building2}
                  label="Agency / institutional"
                  helper="Institutional pricing (+20%) · tax exempt"
                />
              </div>
              {/* Inline tax-exempt override — elevated so admins see it
                  right next to the role picker rather than buried in the
                  "Tax" section below. Auto-turns on for agencies. */}
              <label
                data-testid="editor-tax-exempt-inline"
                className="mt-3 flex items-start gap-3 rounded-xl bg-white border border-black/10 px-3 py-2.5 cursor-pointer hover:border-brand-teal/40"
              >
                <input
                  type="checkbox"
                  checked={!!form.tax_exempt}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, tax_exempt: e.target.checked }))
                  }
                  className="mt-0.5 h-4 w-4 accent-brand-teal"
                />
                <span className="flex-1 min-w-0">
                  <span className="block font-bold text-[12.5px] text-brand-charcoal">
                    Tax exempt
                  </span>
                  <span className="block text-[11px] text-brand-charcoal/60 mt-0.5">
                    Skip NYC sales tax on this invoice. Agencies default to on.
                  </span>
                </span>
              </label>
            </SectionCard>
          )}

          {/* Items */}
          <SectionCard title="Line items">
            {form.items.length === 0 ? (
              <p className="text-[12.5px] text-brand-charcoal/55">
                No items yet — search the catalog below to add some.
              </p>
            ) : (
              <ul className="space-y-2" data-testid="editor-items-list">
                {form.items.map((it) => {
                  const k = lineKey(it);
                  const def = itemDef(it.package_id);
                  const hasOpts = Array.isArray(def?.options) && def.options.length > 0;
                  return (
                  <li
                    key={k}
                    data-testid={`editor-item-${it.package_id}`}
                    className="grid grid-cols-12 items-start gap-3 rounded-xl bg-white border border-black/5 p-3 relative"
                  >
                    <div className="col-span-6 sm:col-span-7 min-w-0">
                      <p className="font-bold text-[13.5px] text-brand-charcoal truncate">
                        {itemName(it.package_id)}
                      </p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-[11.5px] text-brand-charcoal/55">
                          ${lineUnitPrice(it).toFixed(2)} ea
                        </span>
                        <span className="text-[10px] text-brand-charcoal/40">·</span>
                        <label className="inline-flex items-center gap-1 text-[10.5px] text-brand-charcoal/60">
                          Override $
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            data-testid={`editor-price-override-${it.package_id}`}
                            value={
                              it.unit_price_override === undefined ||
                              it.unit_price_override === null
                                ? ""
                                : it.unit_price_override
                            }
                            onChange={(e) => setLineOverride(k, e.target.value)}
                            placeholder="—"
                            className="w-20 h-7 rounded-md bg-brand-cream/50 border border-black/10 px-2 text-[12px] tabular-nums text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-teal/30"
                          />
                        </label>
                      </div>
                      {it.selected_options && (
                        <ul
                          data-testid={`editor-item-modifiers-${it.package_id}`}
                          className="mt-1.5 flex flex-wrap gap-1"
                        >
                          {Object.entries(it.selected_options).map(([grp, vals]) => {
                            const arr = Array.isArray(vals) ? vals : [vals];
                            return arr
                              .filter(Boolean)
                              .map((v, vi) => (
                                <span
                                  key={`${grp}-${vi}-${v}`}
                                  className="inline-flex items-center rounded-full bg-brand-cream/70 border border-black/5 px-2 py-[1px] text-[10.5px] text-brand-charcoal/75"
                                >
                                  <span className="font-bold mr-1 opacity-65">{grp}:</span>
                                  {v}
                                </span>
                              ));
                          })}
                        </ul>
                      )}
                      {hasOpts && (
                        <button
                          type="button"
                          onClick={() => editLineModifiers(it)}
                          data-testid={`editor-edit-modifiers-${it.package_id}`}
                          className="mt-1.5 inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wider text-brand-teal hover:text-brand-charcoal transition-colors"
                        >
                          <SlidersHorizontal className="h-3 w-3" />
                          {it.selected_options ? "Edit modifiers" : "Add modifiers"}
                        </button>
                      )}
                    </div>
                    <div className="col-span-4 sm:col-span-3 inline-flex items-center gap-1 rounded-full bg-brand-charcoal text-white px-1.5 py-1 self-start">
                      <button
                        type="button"
                        data-testid={`editor-dec-${it.package_id}`}
                        onClick={() => setQtyByKey(k, it.quantity - 1)}
                        className="inline-flex h-6 w-6 items-center justify-center rounded-full hover:bg-white/10"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <input
                        data-testid={`editor-qty-${it.package_id}`}
                        type="number"
                        min={1}
                        value={it.quantity}
                        onChange={(e) =>
                          setQtyByKey(k, Math.max(1, Number(e.target.value) || 1))
                        }
                        className="w-10 text-center bg-transparent text-[12px] font-bold tabular-nums focus:outline-none"
                      />
                      <button
                        type="button"
                        data-testid={`editor-inc-${it.package_id}`}
                        onClick={() => setQtyByKey(k, it.quantity + 1)}
                        className="inline-flex h-6 w-6 items-center justify-center rounded-full hover:bg-white/10"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="col-span-2 text-right font-bold text-[13px] text-brand-charcoal tabular-nums self-start">
                      ${(lineUnitPrice(it) * it.quantity).toFixed(2)}
                    </div>
                    <button
                      type="button"
                      data-testid={`editor-remove-${it.package_id}`}
                      onClick={() => removeItemByKey(k)}
                      className="absolute top-2 right-2 text-brand-charcoal/35 hover:text-red-600"
                      aria-label="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                  );
                })}
              </ul>
            )}
            <div className="mt-3">
              <div className="flex items-center gap-2">
                <Input
                  data-testid="editor-catalog-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search the catalog — name or category…"
                  className="h-10 rounded-xl bg-white border-black/10 flex-1"
                />
                <button
                  type="button"
                  data-testid="editor-create-product-toggle"
                  onClick={() => setCreateOpen((v) => !v)}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-brand-teal text-brand-charcoal px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal/85 transition-colors"
                >
                  <Plus className="h-3 w-3" />
                  {createOpen ? "Cancel" : "New product"}
                </button>
              </div>
              {/* One-tap category filter chips — click to narrow the picker
                  below to a single category (e.g. Wings, Sliders, Beverages).
                  Empty state ("All") shows every product. */}
              {existingCategories.length > 0 && (
                <div
                  data-testid="editor-category-chips"
                  className="mt-2 flex flex-wrap gap-1.5"
                >
                  <button
                    type="button"
                    data-testid="editor-category-chip-all"
                    onClick={() => setActiveCategory("")}
                    className={`inline-flex items-center rounded-full border px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.10em] transition-colors ${
                      activeCategory === ""
                        ? "bg-brand-charcoal text-white border-brand-charcoal"
                        : "bg-white text-brand-charcoal border-black/15 hover:border-brand-charcoal"
                    }`}
                  >
                    All
                  </button>
                  {existingCategories.map((cat) => {
                    const active =
                      activeCategory.toLowerCase() === cat.toLowerCase();
                    return (
                      <button
                        key={cat}
                        type="button"
                        data-testid={`editor-category-chip-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => setActiveCategory(active ? "" : cat)}
                        className={`inline-flex items-center rounded-full border px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.10em] transition-colors ${
                          active
                            ? "bg-brand-charcoal text-white border-brand-charcoal"
                            : "bg-white text-brand-charcoal border-black/15 hover:border-brand-charcoal"
                        }`}
                      >
                        {cat}
                      </button>
                    );
                  })}
                </div>
              )}
              {createOpen && (
                <NewProductForm
                  testidPrefix="editor-create-product"
                  categories={existingCategories}
                  onCancel={() => setCreateOpen(false)}
                  onCreated={(item) => {
                    setLocalAdditions((prev) => [item, ...prev]);
                    setForm((st) => ({ ...st, items: [...st.items, { package_id: item.id, quantity: 1 }] }));
                    setCreateOpen(false);
                  }}
                />
              )}
              <ul
                data-testid="editor-catalog-list"
                className="mt-2 max-h-72 overflow-y-auto rounded-xl bg-brand-cream/40 border border-black/5 p-1.5"
              >
                {groupedCatalog.length === 0 ? (
                  <li className="text-center text-[11.5px] text-brand-charcoal/55 py-6">
                    No matching items. Try a different search or hit &quot;New product&quot;.
                  </li>
                ) : (
                  groupedCatalog.map((group) => (
                    <li key={group.category} className="mb-1.5 last:mb-0">
                      <p
                        data-testid={`editor-catalog-header-${group.category}`}
                        className="sticky top-0 z-[1] bg-brand-cream/95 backdrop-blur px-2 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-brand-teal rounded"
                      >
                        {group.category} <span className="text-brand-charcoal/40">· {group.items.length}</span>
                      </p>
                      <ul className="mt-1 space-y-1">
                        {group.items.map((it) => (
                          <li
                            key={it.id}
                            data-testid={`editor-catalog-${it.id}`}
                            className="flex items-center gap-3 rounded-lg bg-white border border-black/5 px-3 py-2"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="font-bold text-[12.5px] text-brand-charcoal truncate">
                                {it.name}
                              </p>
                              <p className="text-[11px] text-brand-charcoal/55">
                                ${itemPrice(it.id).toFixed(2)} · {it.category}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => addItem(it.id)}
                              data-testid={`editor-catalog-add-${it.id}`}
                              className="inline-flex items-center gap-1 rounded-full bg-brand-charcoal text-white px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-colors"
                            >
                              <Plus className="h-3 w-3" />
                              Add
                            </button>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))
                )}
              </ul>
            </div>
          </SectionCard>

          {/* Fulfillment + Customer */}
          <SectionCard title="Customer & fulfillment">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Customer Name *">
                <Input data-testid="editor-customer-name" value={form.customer_name} onChange={update("customer_name")} className="h-10 rounded-xl bg-white border-black/10" />
              </Field>
              <Field label="Email *">
                <Input data-testid="editor-customer-email" type="email" value={form.customer_email} onChange={update("customer_email")} className="h-10 rounded-xl bg-white border-black/10" />
              </Field>
              <Field label="Phone *">
                <Input data-testid="editor-customer-phone" value={form.customer_phone} onChange={update("customer_phone")} className="h-10 rounded-xl bg-white border-black/10" />
              </Field>
              <Field label="Fulfillment">
                <select
                  data-testid="editor-fulfillment"
                  value={form.fulfillment}
                  onChange={update("fulfillment")}
                  className="h-10 rounded-xl bg-white border border-black/10 px-3 text-[13px]"
                >
                  <option value="pickup">Pickup</option>
                  <option value="delivery">Delivery</option>
                </select>
              </Field>
              {form.fulfillment === "delivery" && (
                <>
                  <div className="sm:col-span-2">
                    <Field label="Street Address">
                      <Input
                        data-testid="editor-address"
                        value={form.address}
                        onChange={update("address")}
                        placeholder="123 Main St"
                        autoComplete="address-line1"
                        className="h-10 rounded-xl bg-white border-black/10"
                      />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Apt / Suite / Unit (optional)">
                      <Input
                        data-testid="editor-address-line2"
                        value={form.address_line2}
                        onChange={update("address_line2")}
                        placeholder="Apt 4B, Suite 200, etc."
                        autoComplete="address-line2"
                        className="h-10 rounded-xl bg-white border-black/10"
                      />
                    </Field>
                  </div>
                  <Field label="City">
                    <Input
                      data-testid="editor-city"
                      value={form.city}
                      onChange={update("city")}
                      placeholder="Jamaica"
                      autoComplete="address-level2"
                      className="h-10 rounded-xl bg-white border-black/10"
                    />
                  </Field>
                  <Field label="State">
                    <select
                      data-testid="editor-state"
                      value={form.state}
                      onChange={update("state")}
                      autoComplete="address-level1"
                      className="h-10 rounded-xl bg-white border border-black/10 px-3 text-[13px]"
                    >
                      <option value="">Select state…</option>
                      {US_STATES.map(([code, name]) => (
                        <option key={code} value={code}>
                          {`${code} — ${name}`}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="ZIP Code">
                    <Input
                      data-testid="editor-zip"
                      value={form.zip_code}
                      onChange={update("zip_code")}
                      placeholder="11433"
                      autoComplete="postal-code"
                      inputMode="numeric"
                      maxLength={10}
                      className="h-10 rounded-xl bg-white border-black/10"
                    />
                  </Field>
                  <Field label="Delivery Fee">
                    <Input
                      data-testid="editor-delivery-fee"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.delivery_fee}
                      onChange={update("delivery_fee")}
                      className="h-10 rounded-xl bg-white border-black/10"
                    />
                  </Field>
                </>
              )}
              <Field label={form.fulfillment === "delivery" ? "Drop-off Date" : "Pickup Date"}>
                <Input data-testid="editor-event-date" type="date" value={form.event_date} onChange={update("event_date")} className="h-10 rounded-xl bg-white border-black/10" />
              </Field>
              <Field label={form.fulfillment === "delivery" ? "Drop-off Time" : "Pickup Time"}>
                <Input data-testid="editor-event-time" type="time" value={form.event_time} onChange={update("event_time")} className="h-10 rounded-xl bg-white border-black/10" />
              </Field>
              {form.fulfillment === "delivery" && (
                <>
                  <Field label="On-site Contact Name">
                    <Input data-testid="editor-onsite-name" value={form.on_site_contact_name} onChange={update("on_site_contact_name")} className="h-10 rounded-xl bg-white border-black/10" />
                  </Field>
                  <Field label="On-site Contact Phone">
                    <Input data-testid="editor-onsite-phone" value={form.on_site_contact_phone} onChange={update("on_site_contact_phone")} className="h-10 rounded-xl bg-white border-black/10" />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Delivery Instructions & Access (entry, ramps, floor, loading dock…)">
                      <Textarea
                        data-testid="editor-delivery-instructions"
                        value={form.delivery_instructions}
                        onChange={update("delivery_instructions")}
                        rows={3}
                        maxLength={1000}
                        className="rounded-xl bg-white border-black/10"
                      />
                    </Field>
                  </div>
                  <div className="sm:col-span-2 rounded-2xl bg-brand-cream/40 border border-black/10 p-3">
                    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
                      Receiving logistics
                    </p>
                    <div className="mt-2.5 grid gap-2.5 sm:grid-cols-2">
                      <EditorYesNo
                        testId="editor-receiving-personnel"
                        label="Staff on-site to help receive/carry?"
                        value={form.has_receiving_personnel}
                        onChange={(v) => setForm((s) => ({ ...s, has_receiving_personnel: v }))}
                      />
                      <EditorYesNo
                        testId="editor-receiving-cart"
                        label="Cart or dolly on-site?"
                        value={form.has_delivery_cart}
                        onChange={(v) => setForm((s) => ({ ...s, has_delivery_cart: v }))}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </SectionCard>

          {/* Payment + status */}
          <SectionCard title={mode === "create" ? "Payment method" : "Status & payment"}>
            {mode === "create" ? (
              <>
                <div className="grid sm:grid-cols-3 gap-3">
                  <PickerCard
                    testId="invoice-payment-po"
                    active={form.payment_method === "po"}
                    onClick={() => setForm((s) => ({ ...s, payment_method: "po" }))}
                    icon={FileText}
                    label="Purchase Order"
                    helper="No card charged. Customer pays on standard net terms."
                  />
                  <PickerCard
                    testId="invoice-payment-card"
                    active={form.payment_method === "card"}
                    onClick={() => setForm((s) => ({ ...s, payment_method: "card" }))}
                    icon={CreditCard}
                    label="Stripe payment link"
                    helper="Email includes a secure Pay Now button. Live Stripe."
                  />
                  <PickerCard
                    testId="invoice-payment-cash"
                    active={form.payment_method === "cash"}
                    onClick={() => setForm((s) => ({ ...s, payment_method: "cash" }))}
                    icon={Building2}
                    label="Cash on pickup / delivery"
                    helper="Customer brings cash when receiving the food."
                  />
                </div>
                {form.payment_method === "po" && (
                  <div className="mt-3">
                    <Field label="PO Number *">
                      <Input data-testid="editor-po" value={form.po_number} onChange={update("po_number")} placeholder="PO-2026-XXXX" className="h-10 rounded-xl bg-white border-black/10" />
                    </Field>
                  </div>
                )}
              </>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Status">
                  <select
                    data-testid="editor-status"
                    value={form.status}
                    onChange={update("status")}
                    className="h-10 rounded-xl bg-white border border-black/10 px-3 text-[13px] capitalize"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="PO Number">
                  <Input data-testid="editor-po-edit" value={form.po_number} onChange={update("po_number")} className="h-10 rounded-xl bg-white border-black/10" />
                </Field>
              </div>
            )}
          </SectionCard>

          {/* Notes */}
          <SectionCard title="Notes">
            <div className="grid gap-3">
              <Field label="Customer-facing notes (printed on receipt)">
                <Textarea
                  data-testid="editor-notes"
                  value={form.notes}
                  onChange={update("notes")}
                  rows={2}
                  className="rounded-xl bg-white border-black/10"
                />
              </Field>
              <Field label="Internal notes (admin-only)">
                <Textarea
                  data-testid="editor-internal-notes"
                  value={form.internal_notes}
                  onChange={update("internal_notes")}
                  rows={2}
                  className="rounded-xl bg-white border-black/10"
                />
              </Field>
            </div>
          </SectionCard>

          {/* Totals */}
          <SectionCard title="Discount (optional)">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Amount ($)">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  data-testid="editor-discount-amount"
                  value={form.discount_amount}
                  onChange={update("discount_amount")}
                  placeholder="0.00"
                  className="h-10 rounded-xl bg-white border-black/10"
                />
              </Field>
              <Field label="Label (shown on receipt)">
                <Input
                  data-testid="editor-discount-label"
                  value={form.discount_label}
                  onChange={update("discount_label")}
                  placeholder="e.g. Loyalty discount, First-time customer"
                  className="h-10 rounded-xl bg-white border-black/10"
                />
              </Field>
            </div>
          </SectionCard>

          {/* Tax-exempt override — off by default. When on, the sales-tax
              row is zeroed out for this specific invoice regardless of the
              role_pricing (private / agency). Handy for W-9-verified
              customers on a private invoice, or to remove tax from an
              agency PO retroactively. */}
          <SectionCard title="Tax">
            <label
              data-testid="editor-tax-exempt-toggle"
              className="flex items-start gap-3 rounded-xl bg-white/60 border border-black/10 p-3 cursor-pointer hover:border-brand-teal/40"
            >
              <input
                type="checkbox"
                checked={!!form.tax_exempt}
                onChange={(e) => setForm((s) => ({ ...s, tax_exempt: e.target.checked }))}
                className="mt-0.5 h-4 w-4 accent-brand-teal"
              />
              <span className="flex-1 min-w-0">
                <span className="block font-bold text-[13px] text-brand-charcoal">
                  Tax exempt
                </span>
                <span className="block text-[11.5px] text-brand-charcoal/60 mt-0.5">
                  Skip NYC sales tax on this invoice. Attach the tax-exempt
                  doc to the customer&apos;s file. Agency PO orders are exempt by
                  default — leave off unless you know what you&apos;re doing.
                </span>
              </span>
            </label>
          </SectionCard>

          <SectionCard title="Totals">
            <ul className="text-[13px] space-y-1.5">
              <Row label="Subtotal" value={`$${totals.subtotal.toFixed(2)}`} />
              {totals.discount > 0 && (
                <Row
                  label={
                    form.discount_label
                      ? `Discount · ${form.discount_label}`
                      : "Discount"
                  }
                  value={`-$${totals.discount.toFixed(2)}`}
                />
              )}
              <Row
                label={form.fulfillment === "delivery" ? "Delivery fee" : "Pickup"}
                value={
                  form.fulfillment === "delivery" ? `$${totals.delivery.toFixed(2)}` : "$0.00"
                }
              />
              <Row
                label={
                  totals.isExempt
                    ? "Tax (Tax Exempt)"
                    : `Sales Tax (${(TAX_RATE * 100).toFixed(3).replace(/\.?0+$/, "")}%)`
                }
                value={`$${totals.tax.toFixed(2)}`}
              />
              <li className="h-px bg-black/10 my-1" />
              <Row label="Total" value={`$${totals.total.toFixed(2)}`} bold />
            </ul>
          </SectionCard>

          {error && (
            <div
              data-testid="editor-error"
              role="alert"
              className="inline-flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              data-testid="editor-save-btn"
              onClick={submit}
              disabled={saving || sending}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-6 py-3.5 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors disabled:opacity-60"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {mode === "create" ? "Create Invoice" : "Save Changes"}
            </button>
            {mode === "edit" && (
              <button
                type="button"
                data-testid="editor-send-invoice-btn"
                onClick={sendInvoice}
                disabled={saving || sending}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-white border-2 border-brand-charcoal text-brand-charcoal px-6 py-3.5 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-charcoal hover:text-white transition-colors disabled:opacity-60"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Send Invoice to Customer
              </button>
            )}
            <button
              type="button"
              data-testid="editor-cancel-btn"
              onClick={() => onOpenChange(false)}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-black/10 text-brand-charcoal/70 px-6 py-3.5 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-cream"
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
      <OptionPickerModal
        item={picker?.item || null}
        isOpen={!!picker}
        initialSelections={picker?.initialSelections}
        initialQuantity={
          picker?.editLineKey
            ? form.items.find((it) => lineKey(it) === picker.editLineKey)?.quantity
            : 1
        }
        onClose={() => setPicker(null)}
        onConfirm={handlePickerConfirm}
      />
    </>
  );
};

const SectionCard = ({ title, children }) => (
  <section className="rounded-2xl bg-brand-cream/40 border border-black/5 p-4 sm:p-5">
    <h3 className="font-display font-black text-[14px] text-brand-charcoal tracking-tight mb-3">
      {title}
    </h3>
    {children}
  </section>
);

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
      {label}
    </Label>
    {children}
  </div>
);

// Compact tri-state Yes/No pills for the admin order editor. null = not answered.
const EditorYesNo = ({ testId, label, value, onChange }) => {
  const btn = (v, txt) => {
    const active = value === v;
    return (
      <button
        type="button"
        data-testid={`${testId}-${txt.toLowerCase()}`}
        onClick={() => onChange(active ? null : v)}
        className={`h-7 px-3 rounded-full text-[11px] font-bold uppercase tracking-[0.1em] transition-colors ${
          active
            ? v
              ? "bg-brand-teal text-white"
              : "bg-brand-charcoal text-white"
            : "bg-white text-brand-charcoal/70 border border-black/10 hover:text-brand-charcoal"
        }`}
      >
        {txt}
      </button>
    );
  };
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] text-brand-charcoal/70 leading-snug">{label}</span>
      <div className="flex items-center gap-1.5">
        {btn(true, "Yes")}
        {btn(false, "No")}
      </div>
    </div>
  );
};

const Row = ({ label, value, bold }) => (
  <li
    className={`flex items-center justify-between ${
      bold
        ? "font-display font-black text-[15px] text-brand-charcoal"
        : "text-brand-charcoal/70"
    }`}
  >
    <span>{label}</span>
    <span className="tabular-nums">{value}</span>
  </li>
);

const PickerCard = ({ testId, active, onClick, icon: Icon, label, helper }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    aria-pressed={active}
    className={`flex items-start gap-3 rounded-2xl border-2 p-4 text-left transition-all ${
      active
        ? "border-brand-teal bg-brand-teal/5"
        : "border-black/10 bg-white hover:border-brand-charcoal/40"
    }`}
  >
    <span
      className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
        active ? "bg-brand-teal text-white" : "bg-brand-cream text-brand-charcoal/70"
      }`}
    >
      <Icon className="h-5 w-5" />
    </span>
    <div className="min-w-0">
      <p className="font-bold text-[13.5px] text-brand-charcoal leading-tight">{label}</p>
      <p className="mt-1 text-[11.5px] text-brand-charcoal/60 leading-snug">{helper}</p>
    </div>
  </button>
);

export default OrderEditorModal;
